import type {
  IndicativeRange,
  LmiCostOutput,
  PayNowVsSaveOutput,
  RefundOnDischargeOutput,
} from "./types.js";

// Plain-English result copy for LMI. Lives in the rules package so every surface
// (Expo result screen, "my results" list, PDF report) renders identical wording
// from one source — that matters for regulation, not just DRY.
//
// Every string here is INFORMATION, not advice (validation §3): we never say
// "save $X", "you should pay LMI", "buy now", "switch lender", or name a
// broker/loan to choose. The premium is always framed as an INDICATIVE RANGE
// ("indicative — your lender's actual LMI may differ"); the break-even is always
// a CONDITIONAL sensitivity band on the user's own price-movement assumption,
// never a "you'll be ahead" verdict; the refund always LEADS with disqualifiers
// and never states a definite "you're owed $X". Pure string-builders, tested.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Render an indicative dollar range — NEVER a single "your fee" figure. */
export function formatRange(range: IndicativeRange): string {
  if (range.low === range.high) return formatAud(range.low);
  return `${formatAud(range.low)}–${formatAud(range.high)}`;
}

function pct(fraction: number): string {
  return `${+(fraction * 100).toFixed(1)}%`;
}

// ---- lmiCost ----

export function lmiCostHeadline(out: LmiCostOutput): string {
  if (out.waivedByFirstHomeGuarantee) {
    return "You may not need to pay LMI at all — the First Home Guarantee can waive it";
  }
  if (!out.lmiApplies) {
    return "No LMI applies at this deposit — your loan is at or below 80% LVR";
  }
  return `Indicative LMI for a ${pct(out.lvr)} LVR loan: ${formatRange(
    out.indicativePremium,
  )}`;
}

/** Always states the premium as a dated, sourced, indicative RANGE. */
export function lmiCostBullet(out: LmiCostOutput): string {
  if (out.waivedByFirstHomeGuarantee) {
    return `On these numbers your loan is above 80% LVR (${out.lvrBand}), where LMI would normally apply — but if you're eligible for the First Home Guarantee (5% Deposit Scheme) the Commonwealth guarantees the deposit gap and the lender requires no LMI, so the indicative cost is ${formatAud(
      0,
    )}.`;
  }
  if (!out.lmiApplies) {
    return `Your loan is ${pct(
      out.lvr,
    )} of the property value (${out.lvrBand}). LMI is generally only required above 80% LVR, so on these numbers no LMI applies.`;
  }
  return `Your loan is ${pct(out.lvr)} of the property value, which falls in the ${
    out.lvrBand
  } band. The indicative LMI premium is ${formatRange(
    out.indicativePremium,
  )}. This is an estimate from indicative rates — your lender's actual LMI may differ. It is not a quote.`;
}

/** The "one tier down" cliff framing — descriptive, not a nudge to buy/borrow. */
export function nextTierBullet(out: LmiCostOutput): string | null {
  if (out.nextTierDown === null) return null;
  return `A larger deposit lowers the fee in steps: reaching ${pct(
    out.nextTierDown.atLvr,
  )} LVR — about ${formatAud(
    out.nextTierDown.extraDeposit,
  )} more deposit — would move you into the cheaper LVR band below.`;
}

// ---- payNowVsSaveTo20 ----

export function payNowVsSaveHeadline(out: PayNowVsSaveOutput): string {
  if (out.alreadyAt20) {
    return "You're already at a 20% deposit — no LMI and nothing to wait for";
  }
  return `Buying now means an indicative LMI of ${formatRange(
    out.indicativePremiumNow,
  )}; here's how long saving to 20% could take under different price assumptions`;
}

/**
 * The sensitivity band — one line per USER price-movement assumption. We never
 * collapse it to "buy now and you're $X ahead": the verdict flips with the
 * assumption, so a single answer would be a disguised forecast.
 */
export function saveTo20Bullets(out: PayNowVsSaveOutput): string[] {
  if (out.alreadyAt20) {
    return [
      "Your deposit already meets 20% of the property value, so no LMI would apply and there's no wait to model.",
    ];
  }
  return out.scenarios.map((s) => {
    const assumption =
      s.priceMovementAssumption === 0
        ? "if prices stay flat"
        : `if prices move ${pct(s.priceMovementAssumption)} a year`;
    if (s.yearsToReach20 === null) {
      return `At your current savings rate, ${assumption} a 20% deposit isn't reached within the modelled period — the deposit target grows at least as fast as you save.`;
    }
    return `${assumption[0]!.toUpperCase()}${assumption.slice(
      1,
    )}, reaching a 20% deposit takes about ${s.yearsToReach20} year${
      s.yearsToReach20 === 1 ? "" : "s"
    }. These are your own assumptions, not a forecast.`;
  });
}

// ---- refundOnDischarge ----

/** LEADS with disqualifiers. Never states "you're owed $X". */
export function refundHeadline(out: RefundOnDischargeOutput): string {
  if (out.insurerDiscontinuedRefunds) {
    return "Your insurer no longer offers LMI refunds — no refund is payable";
  }
  if (out.disqualifiers.length > 0) {
    return "Based on these answers, no LMI refund appears payable — check the reasons below";
  }
  if (out.indicativeRefundRange) {
    return `An LMI refund may be possible (${out.tierLabel}) — confirm the amount with your lender`;
  }
  return "Whether an LMI refund is possible depends on your lender — confirm with them";
}

export function refundBullets(out: RefundOnDischargeOutput): string[] {
  const bullets: string[] = [];
  // Disqualifiers first, always.
  for (const d of out.disqualifiers) bullets.push(d);

  if (out.potentiallyEligible && out.indicativeRefundRange) {
    bullets.push(
      `If your insurer still refunds, an indicative refund for discharge ${out.tierLabel} could be in the order of ${formatRange(
        out.indicativeRefundRange,
      )}, after the non-refundable stamp duty on the premium. This is indicative only — refund scales vary by insurer and lender, are declining, and the refund is paid by your lender and must be requested. Confirm the actual amount with your lender.`,
    );
  }
  return bullets;
}

export function sourceBullet(ruleVersion: string, verifiedAt: string): string {
  return `LMI premiums and refund scales here are indicative estimates from published rate guides and insurer material (ruleset ${ruleVersion}, last verified ${verifiedAt}) — there is no single public LMI price list, and your lender's actual figures may differ. Always confirm with your lender. Educational tool. Not financial advice.`;
}
